import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  Divider,
  Form,
  Input,
  Radio,
  Tooltip,
  Typography,
} from '@robocorp/components';
import { isSafeName } from '@robocorp/robocloud-shared-utils';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useActions, useSelector } from '../../../../hooks';
import { useCreateOrganizationMutation, useCreateWorkspaceMutation } from '../../../../queries';
import { uiSelectors } from '../../../../selectors';

const CreateOrganizationDialog = () => {
  const [error, setError] = useState<boolean | string>(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const createOrganizationMutation = useCreateOrganizationMutation();
  const createWorkspaceMutation = useCreateWorkspaceMutation();

  const schema = useMemo(
    () =>
      z.object({
        organizationName: z
          .string()
          .min(1)
          .max(255)
          .refine(isSafeName, {
            message: t('nameContainsUrl'),
          }),
        workspaceName: z
          .string()
          .min(1)
          .max(255)
          .refine(isSafeName, {
            message: t('nameContainsUrl'),
          }),
        mainBusiness: z.string(),
        teamCount: z.string(),
        purpose: z.string(),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    defaultValues: {
      organizationName: '',
      workspaceName: '',
    },
    resolver: zodResolver(schema),
  });

  const mainBusiness = watch('mainBusiness');
  const teamCount = watch('teamCount');
  const purpose = watch('purpose');

  const { canCreateOrganization, currentUser, organizationsDialogOpen, organizationLimit } =
    useSelector((state) => ({
      currentUser: state.userResources.users.current,
      organizationsDialogOpen: state.ui.organizationsDialogOpen,
      canCreateOrganization: uiSelectors.canCreateOrganization(state),
      organizationLimit: state.userResources.users.current.limit_overrides.maxOrganizations,
    }));

  const { toggleOrganizationDialog, setCurrentOrganization, setCurrentWorkspace } = useActions(
    ({ uiActions }) => ({
      toggleOrganizationDialog: uiActions.toggleOrganizationDialog,
      setCurrentOrganization: uiActions.setCurrentOrganizationId,
      setCurrentWorkspace: uiActions.setCurrentWorkspaceId,
    }),
  );

  const businesses = useMemo(
    () => [
      { label: t('finance'), value: 'finance' },
      { label: t('retail'), value: 'retail' },
      { label: t('healthCare'), value: 'healthCare' },
      { label: t('industrial'), value: 'industrial' },
      { label: t('publicServices'), value: 'publicServices' },
      { label: t('education'), value: 'education' },
      { label: t('consulting'), value: 'consulting' },
      { label: t('informationTechnology'), value: 'informationTechnology' },
      { label: t('hobbyProjects'), value: 'hobbyProjects' },
      { label: t('other'), value: 'other' },
    ],
    [t],
  );

  const counts = useMemo(
    () => [
      { label: t('justMe'), value: 'justMe' },
      { label: `2-5 ${t('people')}`, value: '2-5' },
      { label: `5-20 ${t('people')}`, value: '5-20' },
      { label: `20-100 ${t('people')}`, value: '2-100' },
      { label: `100-1000 ${t('people')}`, value: '100-1000' },
      { label: `${t('moreThan', { amount: 1000 })}`, value: 'moreThan1000' },
    ],
    [t],
  );

  const purposeItems = useMemo(
    () => [
      { label: t('purpose.me'), value: 'me' },
      { label: t('purpose.org'), value: 'organization' },
      { label: t('purpose.client'), value: 'client' },
    ],
    [t],
  );

  const onClose = useCallback(() => {
    window.setTimeout(() => {
      reset();
    }, 200);
    toggleOrganizationDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreateOrganization = handleSubmit(async (data) => {
    if (!currentUser.details.email) {
      return;
    }

    createOrganizationMutation.mutate(
      {
        name: data.organizationName,
        email: currentUser.details.email,
        mainBusiness,
        teamCount,
        purpose: data.purpose,
      },
      {
        onSuccess: (newOrganization) => {
          createWorkspaceMutation.mutate(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            { organizationId: newOrganization!.id, name: data.workspaceName },
            {
              onSuccess: (newWorkspace) => {
                if (!newOrganization || !newWorkspace) return;

                setCurrentOrganization(newOrganization.id);
                setCurrentWorkspace(newWorkspace.id);

                onClose();
                navigate(`/${newOrganization.short_name}/${newWorkspace.short_name}/processes`);
              },
              onError: () => {
                setError(true);
              },
            },
          );
        },
        onError: () => {
          setError(true);
        },
      },
    );
  });

  const onMainBusinessChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue('mainBusiness', event.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTeamCountChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue('teamCount', event.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPurposeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue('purpose', event.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = createOrganizationMutation.isLoading || createWorkspaceMutation.isLoading;

  return (
    <Dialog size="medium" open={organizationsDialogOpen} onClose={onClose} width={880}>
      <Form onSubmit={onCreateOrganization}>
        <Dialog.Header>
          <Dialog.Header.Title title={`${t('createNewOrganization')}`} />
          <Dialog.Header.Description>
            <strong>{t('createOrganizationDescriptionShort')}</strong>{' '}
            {t('createOrganizationDescriptionLong')}
          </Dialog.Header.Description>
        </Dialog.Header>
        <Dialog.Content>
          <Form.Fieldset>
            <Form.Columns>
              <Input
                autoFocus
                label={t('organizationName')}
                description={t('organizationNameDesc')}
                type="text"
                data-testid="organization__input-name"
                id="new-organization__name"
                error={errors.organizationName?.message}
                {...register('organizationName')}
              />

              <Input
                label={t('workspace')}
                description={t('workspaceDesc')}
                type="text"
                data-testid="organization__input-workspace"
                id="new-organization__workspace"
                error={errors.workspaceName?.message}
                {...register('workspaceName')}
              />
            </Form.Columns>
            <Divider />
            <Form.Columns>
              <Box>
                <Typography mb={16}>{t('askForTeamCount')}</Typography>
                <Radio.Group
                  onChange={onTeamCountChange}
                  value={teamCount}
                  data-testid="organization__input-people"
                  id="new-organization__people"
                >
                  {counts.map(({ label, value }) => (
                    <Radio key={value} value={value} label={label} />
                  ))}
                </Radio.Group>
              </Box>
              <Box>
                <Typography mb={16}>{t('askForBusiness')}</Typography>
                <Radio.Group
                  onChange={onMainBusinessChange}
                  value={mainBusiness}
                  data-testid="organization__input-business"
                  id="new-organization__business"
                >
                  {businesses.map(({ label, value }) => (
                    <Radio key={value} value={value} label={label} />
                  ))}
                </Radio.Group>
                <Box mt={32}>
                  <Typography mb={16}>{t('askForPurpose')}</Typography>
                  <Radio.Group
                    onChange={onPurposeChange}
                    value={purpose}
                    data-testid="organization__input-purpose"
                    id="new-organization__purpose"
                  >
                    {purposeItems.map(({ label, value }) => (
                      <Radio key={value} value={value} label={label} />
                    ))}
                  </Radio.Group>
                </Box>
              </Box>
            </Form.Columns>
            {error && (
              <Box color="content.error" textAlign="right" pb={16}>
                {t('errorBoundary1')}
              </Box>
            )}
          </Form.Fieldset>
        </Dialog.Content>
        <Dialog.Actions>
          {!canCreateOrganization ? (
            <Tooltip
              placement="left"
              text={t('cannotCreateMoreOrganizations', {
                limit: organizationLimit,
              })}
            >
              <Button disabled>{t('create')}</Button>
            </Tooltip>
          ) : (
            <Button
              id="new-organization__create"
              data-testid="organization__create-submit"
              type="submit"
              disabled={!teamCount || !mainBusiness || !purpose}
              loading={isLoading}
            >
              {t('create')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
        </Dialog.Actions>
      </Form>
    </Dialog>
  );
};

export default CreateOrganizationDialog;
